import { IconClockHistory } from "components/Icons/IconClockHistory";
import { IconLightning } from "components/Icons/IconLightning";
import { IconTerminalWindow } from "components/Icons/IconTerminalWindow";
import { PageHead } from "gui/head/head";
import { PageMain } from "gui/page/page";
import { GetAllV2TeamServiceTiersQuery } from "lib/types/graphql";
import { Header } from "shared/Header/Header";
import { PricingCards } from "./plans/PricingCards";
import styles from "./PricingPage.module.css";
import cn from "classnames";
import { Tooltip } from "components/Tooltip/Tooltip";
import { Quotes } from "./Quotes";
import { CustomPlanBanner } from "./CustomPlanBanner";
import { Faqs } from "./Faqs";
import { useWindowSize } from "@reach/window-size";
import { ApiBanner } from "./ApiBanner";
import Image from "next/image";

import { useRouter } from "next/router";
import { useEffect } from "react";
import { useSession } from "gui/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";

import { frontend as getUrlOrigin } from "lib/env/env";
import { AnchorButton } from "components/Button/AnchorButton";
import { ComparePlans } from "./ComparePlans";

export interface Props {
  teamServiceTiers: GetAllV2TeamServiceTiersQuery["team_service_tiers"];
}

const comparePlansId = "compare-plans";

const PricingPage = ({ teamServiceTiers }: Props) => {
  const activeContext = useActiveContext();
  const { width } = useWindowSize();
  const isMobile = width < 600;

  useGoToMigrationPageIfLegacyPlanUser();

  const onPricingCardClick = () => {
    window.document.querySelector(`#${comparePlansId}`)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <>
      <PageHead
        title={"Pricing"}
        screenshot={getUrlOrigin() + "/assets/og-pricing.png"}
      />
      <Header />
      <PageMain className={styles.main}>
        <div className={styles.header}>
          Boost your team's Dune experience with
          <div>
            <Tooltip
              label={
                <Image
                  src="/assets/feature-tooltip-hardware.gif"
                  alt="faster-executions"
                  width={448}
                  height={280}
                />
              }
              className={styles.previewTooltip}
              position="below-center"
              hidden={isMobile}
            >
              <div className={cn(styles.title, styles.executions)}>
                <>
                  <IconLightning />
                  faster executions,
                </>
              </div>
            </Tooltip>
            <Tooltip
              label={
                <Image
                  src="/assets/feature-tooltip-api.gif"
                  alt="api-export"
                  width={448}
                  height={280}
                />
              }
              className={styles.previewTooltip}
              position="below-center"
              hidden={isMobile}
            >
              <div className={cn(styles.title, styles.export)}>
                <IconTerminalWindow />
                api export,
              </div>
            </Tooltip>
            <Tooltip
              label={
                <Image
                  src="/assets/feature-tooltip-programmatically.gif"
                  alt="programmatic-queries"
                  width={448}
                  height={280}
                />
              }
              className={styles.previewTooltip}
              position="below-center"
              hidden={isMobile}
            >
              <div className={cn(styles.title, styles.queries)}>
                <IconClockHistory />
                programmatic queries.
              </div>
            </Tooltip>
          </div>
        </div>
        <div className={styles.pricingCards}>
          {Boolean(activeContext?.serviceTier.hasCustomPlan) && (
            <div className={cn(styles.customPlan)}>
              <p>
                This {activeContext?.type} runs on a custom plan. To update plan
                or billing details, please reach out to us.
              </p>
              <AnchorButton
                href="mailto:sales@dune.com"
                size="M"
                theme="primary"
              >
                Contact us
              </AnchorButton>
            </div>
          )}
          <PricingCards
            teamServiceTiers={teamServiceTiers}
            origin="pricing"
            onCardOpen={onPricingCardClick}
          />
        </div>

        <ApiBanner />

        <ComparePlans id={comparePlansId} teamServiceTiers={teamServiceTiers} />

        <div className={styles.customPlanSection}>
          <Quotes />
          <CustomPlanBanner />
        </div>
        <Faqs />
      </PageMain>
    </>
  );
};

export default PricingPage;

/*
 * We have a special version of the pricing page for users on the
 * legacy paid plans. We want to redirect them to that one _if_ they
 * have selected their user as activeContext.
 *
 * (Only relevant if they have Thug Life or Elite. Analyzooor users will
 * be migrated up front.)
 */
function useGoToMigrationPageIfLegacyPlanUser() {
  const router = useRouter();

  const activeContext = useActiveContext();
  const session = useSession();
  const currentPlanId = session?.user?.user_service_tier.id;

  useEffect(() => {
    if (!activeContext) {
      return;
    }
    if (activeContext.type === "user") {
      switch (currentPlanId) {
        case 3: // Thug Life
        case 4: // Elite
          router.replace("/subscription/migrate");
      }
    }
  }, [router, activeContext, currentPlanId]);
}
